import * as React from "react";
import { useRef, useState, useEffect } from "react";
import CabinetSelection from "../components/CabinetSelection";
import Floor from "../models/Floor";
import Room from "../models/Room";
import BaseDialog, { Field, FieldSet } from "./BaseDialog";
import Page from "../models/Page";
import { showToast } from "../components/Toasts";

import Logger from "../Logger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import Cabinet from "../models/Cabinet";

import getRoomOptionsList from "../components/Quickstart/RoomOptionsList";
import { EventKey } from "../eventLogger/EventKeys";
import { itx } from "../translationItx/itx";

import { ShowRoomSubmittalsBackgroundInstantSet } from "../components/ShowRoomSubmittalsBackground";

const logger = new Logger();

export default function RoomDialog(props: {
  parent: Floor;
  obj?: Room;
  cabinetIdFromCreation?: string;
  fromSubmittals?: { background: string; tag: string; name: string; cabinetId: string };
  createNewRoomInFloorDrawing?: boolean;
  onCreated?: (room: Room) => void;
}) {
  const { parent, obj, cabinetIdFromCreation, fromSubmittals, createNewRoomInFloorDrawing } = props;
  const isC1Project = state.projectMetadata.isC1Project;

  const [update, setUpdate] = useState(1);
  let initialTag = "";
  if (!createNewRoomInFloorDrawing) {
    initialTag =
      obj?.tag ||
      fromSubmittals?.tag ||
      state.nameAndTagManager.generateTag(new Room({ isTemporary: true }, parent));
  }
  const [tag, setTag] = useState(initialTag);

  let initialRoomName = "";
  if (!createNewRoomInFloorDrawing) {
    initialRoomName = obj?.name || fromSubmittals?.name || getNewRoomName(tag);
  }
  const [name, setName] = useState(initialRoomName);

  const [cabinetId, setCabinetId] = useState(obj?.cabinetId || fromSubmittals?.cabinetId || "");
  const [background, setBackground] = useState(fromSubmittals?.background || "");
  const tagIsSetManual = useRef<boolean>(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);

  const edit = obj != undefined;

  const options: any = [];

  const roomOptions = getRoomOptionsList();

  roomOptions.forEach((o) => {
    options.push(<option value={o[0]}>{o[0]}</option>);
  });

  useEffect(() => {
    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current?.focus();
      }
    }, 100);
  }, []);

  if (cabinetIdFromCreation && cabinetId == "") {
    setCabinetId(cabinetIdFromCreation);
  }

  if (!edit && parent == undefined) {
    logger.error("parent == undefined");
  }

  const floorCabinet = state.objectRegistry.getObject(obj?.cabinetId);
  const floorCabinetName = floorCabinet?.name;

  function openSubmittals() {
    const newpage = new Page({});

    state.dialogManager.openDialog(
      <ShowRoomSubmittalsBackgroundInstantSet
        page={newpage}
        onChange={() => {
          setBackground(newpage.backgroundImgUrl);
          setUpdate(update + 1);
        }}
        fromRoomCreation={{ parent: parent, tag: tag, name: name, cabinetId: cabinetId }}
      />,
    );
  }

  // check if name is already assingned
  function checkValidName(name: string): boolean {
    var double = false;

    parent?.rooms.forEach((room) => {
      if (state.noDoubleValue.ciEquals(room.name, name)) {
        double = true;
      }
    });

    return !double;
  }

  function getNewRoomName(tag: string): string {
    var roomName = `${state.translate("components.Room", "Raum ")} `;

    var i = 1;
    const numbFromTag = parseInt(tag.replace(/\D/g, ""));
    if (!isNaN(numbFromTag)) {
      i = numbFromTag;
    }

    var newRoomName = roomName + i;
    while (!checkValidName(newRoomName)) {
      newRoomName = roomName + i;
      i++;
    }

    return newRoomName;
  }

  function handleOpen(newRoom: Room) {
    if (newRoom.page == undefined) {
      newRoom.page = new Page({}, newRoom);
    }

    const pageId = newRoom.page.id;

    if (state.activePanes.isOpen(pageId)) {
      state.activePanes.activatePane(pageId);
    } else {
      state.activePanes.loadPage(pageId);
    }
  }

  function onChangeBackgroundImgUrl(backgroundImgUrl: string, page: Page) {
    async function getImagevirtualElement(url: string) {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    }
    const mainDrawingArea = document.getElementById("mainDrawingArea");
    let imageHeight = 0;
    let imageWidth = 0;

    let areaWidth = 21 * 33;
    let areaHeight = 29.7 * 33;
    if (mainDrawingArea) {
      areaWidth = mainDrawingArea.offsetHeight;
      areaHeight = mainDrawingArea.offsetWidth;
    }
    page.resizeImageAfterLoad(backgroundImgUrl).then((result) => {
      page.backgroundImgUrl = result as string;
      getImagevirtualElement(result as string).then((img) => {
        imageWidth = img.naturalWidth;
        imageHeight = img.naturalHeight;
        page.backgroundImagePosition.posX = (areaHeight - imageHeight) / 2;
        page.backgroundImagePosition.posY = (areaWidth - imageWidth) / 2;
        page.raiseOnChange("_backgroundImgUrl");
      });
    });
  }

  function updateTagViaName(newValue: string) {
    if (!tagIsSetManual.current || tag == "") {
      const dummyRoom = new Room({ isTemporary: true }, parent);
      parent.rooms.addWithoutUpdate(dummyRoom);
      dummyRoom.name = newValue;
      dummyRoom.generateTagFromName();

      const tag = dummyRoom.tag;
      if (tagInputRef.current) {
        tagInputRef.current.value = tag;
      }

      parent.rooms.removeWithoutUpdate(dummyRoom);
    }
  }

  function createNewRoom(): boolean {
    let tagFromInput = (document.getElementById("RoomDialog_tag") as HTMLInputElement)?.value;
    if (!tagFromInput) {
      tagFromInput = tag;
    }
    let nameFromInput = (document.getElementById("RoomDialog_name") as HTMLInputElement)?.value;
    if (!nameFromInput) {
      nameFromInput = name;
    }

    //check if tag is double
    const listAllRoomsTagInFloor = new Array<string>();
    parent.rooms.forEach((room) => {
      listAllRoomsTagInFloor.push(room.tag);
    });
    if (createNewRoomInFloorDrawing) {
      if (obj) {
        obj.name = nameFromInput;
        if (!tagFromInput) {
          obj?.generateTagFromName();
        } else {
          obj.tag = tagFromInput;
        }
        obj.cabinetId = cabinetId;
        obj.raiseOnChange();
        state.selectionManager.selectedObject = obj;
      }
    }
    if (edit) {
      return true;
    } else {
      if (!tagFromInput) {
        //setTag(getNewCabinTag());//doesn`t get new Render
        showToast({
          text: state.translate("roomDialog.tagUndefined", `Kein Kennzeichen eingegeben!`),
        });
        return false;
      } else if (!listAllRoomsTagInFloor.includes(tagFromInput)) {
        let xcabinetId = cabinetId;

        if (xcabinetId == "create") {
          //
          // neuen Schaltschrank für den Raum anlegen
          //
          const newCabinet = new Cabinet(
            { name: itx("components.Cabinet", "Schaltschrank") + " " + name },
            state.cabinets,
          );
          newCabinet.tag = state.nameAndTagManager.generateTag(newCabinet);
          state.cabinets.add(newCabinet);
          xcabinetId = newCabinet.id;
          setCabinetId(xcabinetId);
        }

        const newRoom = new Room({ name: nameFromInput, tag: tagFromInput, cabinetId: xcabinetId }, parent);
        if (!isC1Project) {
          if (!newRoom.page) newRoom.page = new Page({}, newRoom);
          onChangeBackgroundImgUrl(background, newRoom.page);
        }
        parent?.rooms.add(newRoom);
        state.eventLogger.addEvent(EventKey.ObjectCreated, {
          objectType: "Room",
          obj: newRoom.id,
        });
        // C1-Räume werden später im Etagenplan platziert; dafür keinen klassischen Raumplan öffnen.
        if (props.onCreated) props.onCreated(newRoom);
        else if (isC1Project) state.selectionManager.selectedObject = newRoom;
        else handleOpen(newRoom);
        return true;
      } else {
        //setTag(getNewCabinTag());//doesn`t get new Render
        showToast({
          text: state.translate(
            "roomDialog.tagAlreadyUsed",
            `Ihr eingegebner Tag ist schon an deinem anderen Raum auf dieser Etage vergeben bitte ändern Sie diesen`,
          ),
        });
        return false;
      }
    }
  }

  return (
    <BaseDialog
      title={
        edit && !createNewRoomInFloorDrawing
          ? state.translate("roomDialog.editRoom", "Raum bearbeiten")
          : itx("roomDialog.createRoom", "Neuen Raum erzeugen", { T1: `${parent.tag} ${parent.fullname}` })
      }
      // onClose={() => {
      //     return true
      // }}

      enterAsOk={true}
      hideXButton={true}
      okCaption={itx("floorDialog.create")}
      onAbort={() => {
        if (createNewRoomInFloorDrawing) {
          obj?.delete(); // undo not added before
        }
        return true;
      }}
      onOk={() => {
        return createNewRoom();
      }}
    >
      {/* {!edit && (
        <div>
           {state.translate(
            "roomDialog.createHereNewRoom",
            "Du legst hier einen neuen Raum an.",
          )}{" "}
          <br /> 
          {state.translate(
            "roomDialog.selectBackgroundImage",
            "Wähle eine Hintergrundzeichnung (Grundriss) für den Raum aus.",
          )}{" "}
          <br />
          {state.translate(
            "roomDialog.possibleSelectingLater",
            "Du kannst auch später noch eine Hintergrundzeichnung auswählen.",
          )}{" "}
          <br />
        </div>
      )} */}

      <FieldSet>
        <Field label={state.translate("name", "Name")}>
          <div style={{ position: "relative", height: "30px" }}>
            <select
              className="input"
              style={{ position: "absolute", width: "100%", left: "0px", top: "0px" }}
              onChange={(ev) => {
                if (nameInputRef.current) {
                  nameInputRef.current.value = ev.currentTarget.value;
                  updateTagViaName(ev.currentTarget.value);
                }
              }}
            >
              {options}
            </select>

            <input
              className="input"
              style={{
                position: "absolute",
                width: "calc(100% - 20px)",
                left: "0px",
                top: "0px",
                zIndex: 10,
                borderTopRightRadius: "0px",
                borderBottomRightRadius: "0px",
              }}
              id="RoomDialog_name"
              ref={nameInputRef}
              maxLength={state.maxCharLimit.text}
              defaultValue={name}
              onInput={(ev) => {
                updateTagViaName(ev.currentTarget.value);
              }}
              onBlur={(ev) => {
                const r = ev.currentTarget.value;
                setName(r);
              }}
            ></input>
          </div>
        </Field>

        <Field label={state.translate("tag", "Tag")}>
          <input
            className="input"
            id="RoomDialog_tag"
            ref={tagInputRef}
            maxLength={state.maxCharLimit.tag}
            defaultValue={tag}
            onChange={(ev) => {
              const v = ev.target.value.toLocaleUpperCase().trim();
              ev.target.value = v;
              setTag(v);
              tagIsSetManual.current = true;
            }}
          ></input>
        </Field>

        <Field label={state.translate("components.Cabinet", "Schaltschrank")}>
          <CabinetSelection
            forRoom={true}
            floorCabinetName={floorCabinetName}
            style={{ width: undefined }}
            value={cabinetId}
            onChange={(id) => {
              setCabinetId(id);
            }}
          ></CabinetSelection>
        </Field>

        {!edit && !isC1Project && (
          <Field label={state.translate("roomDialog.backgroundImage", "Hintergrundzeichnung")}>
            {background != "" && (
              <div style={{ marginLeft: "auto", width: "fit-content" }}>
                <img key={background} style={{ height: "50px", paddingRight: "50px" }} src={background} />
                <button
                  onClick={() => {
                    setBackground("");
                  }}
                  className="button circle"
                >
                  <FontAwesomeIcon
                    style={{
                      height: "14px",
                      color: "var(--fieldBorderColor)",
                    }}
                    icon={faTrash}
                  />
                </button>
              </div>
            )}
            {background == "" && (
              <button
                onClick={() => openSubmittals()}
                className="button"
                data-tooltip-id="tv-tooltip"
                data-tooltip-content={state.translate("step3.selectFromSubmissions", "aus Vorlagen auswählen")}
                data-tooltip-place="top" // no itx
              >
                {itx("floorDialog.selectBuildingPlan", "Gebäudeplan auswählen")}
              </button>
            )}
          </Field>
        )}
      </FieldSet>
    </BaseDialog>
  );
}

export async function openRoomDialog(newRoom: Room, floor: Floor) {
  const ok = await state.dialogManager.openDialog(
    <RoomDialog createNewRoomInFloorDrawing={true} obj={newRoom} parent={floor} />,
  );
  if (ok) {
    state.selectionManager.selectedObject = newRoom;
  } else {
    floor.rooms.remove(newRoom);
  }
}
